// @ts-check
// Persistencia POR USUARIO de los borradores locales de los editores, sin tocar
// el interior de los editores (zona protegida). Vive en la capa `state`: sin
// DOM, sin `fetch`.
//
// Contexto: los tres editores (diagrama/pseudocódigo/python) autoguardan el
// trabajo en `localStorage` como comodidad de recuperación ante recargas (NO es
// la fuente de verdad; el guardado en cuenta es una tarea futura). Sus claves
// son fijas por editor (o, en el diagrama, no reciben el id de usuario), así que
// dos alumnos en el MISMO navegador compartían borrador.
//
// Comportamiento deseado (petición del usuario): al cerrar sesión se GUARDA el
// borrador del usuario que sale; al iniciar sesión se RESTAURA el del usuario
// que entra (el mismo recupera su trabajo; otro distinto ve el suyo, o nada si
// es nuevo). Se implementa moviendo, desde la capa de sesión, las claves de
// `localStorage` del editor entre unas claves "de trabajo" (las que el editor
// lee/escribe) y una copia de seguridad POR USUARIO:
//   - trabajo:  `progedu.<editor>-editor.*` (autoguardado + cronómetro `.intento`)
//   - respaldo: `progedu.session-drafts.<usuarioId>` (un blob JSON por usuario)
// El editor sigue leyendo/escribiendo SIEMPRE las claves de trabajo; este módulo
// solo las intercambia en el borde de sesión (login/registro/logout).

/**
 * Prefijos de namespace de `localStorage` cuyos valores son borradores locales
 * de un editor (autoguardado o cronómetro de intento). Son las claves "de
 * trabajo" que el editor usa directamente.
 */
export const PREFIJOS_BORRADORES_EDITOR = /** @type {const} */ ([
  'progedu.diagram-editor.',
  'progedu.pseudocode-editor.',
  'progedu.python-editor.',
]);

/** Prefijo de las copias de seguridad por usuario (NO es una clave de trabajo). */
export const PREFIJO_RESPALDO_SESION = 'progedu.session-drafts.';

/**
 * @param {Storage} [almacen]
 * @returns {Storage | null}
 */
function obtenerStorage(almacen) {
  try {
    return almacen ?? globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * @param {string} clave
 * @returns {boolean}
 */
function esClaveDeTrabajo(clave) {
  return PREFIJOS_BORRADORES_EDITOR.some((prefijo) => clave.startsWith(prefijo));
}

/**
 * Recoge todas las claves de trabajo presentes en el almacenamiento (sin
 * eliminarlas). Se recogen primero para poder borrar/leer después sin que el
 * índice se desplace.
 * @param {Storage} storage
 * @returns {string[]}
 */
function recogerClavesDeTrabajo(storage) {
  /** @type {string[]} */
  const claves = [];
  try {
    for (let indice = 0; indice < storage.length; indice += 1) {
      const clave = storage.key(indice);
      if (clave && esClaveDeTrabajo(clave)) claves.push(clave);
    }
  } catch {
    return [];
  }
  return claves;
}

/**
 * Elimina de `localStorage` todos los borradores locales de los editores
 * (claves de trabajo). No toca las copias de seguridad por usuario. Silencioso
 * si el almacenamiento no está disponible.
 * @param {Storage} [almacen]
 * @returns {number} Número de claves eliminadas.
 */
export function limpiarBorradoresLocales(almacen) {
  const storage = obtenerStorage(almacen);
  if (!storage) return 0;
  let eliminadas = 0;
  for (const clave of recogerClavesDeTrabajo(storage)) {
    try {
      storage.removeItem(clave);
      eliminadas += 1;
    } catch {
      // Ignorado: no romper el flujo de sesión por un fallo de removeItem.
    }
  }
  return eliminadas;
}

/**
 * GUARDA (stash) los borradores de trabajo actuales en la copia de seguridad de
 * `usuarioId` y luego LIMPIA las claves de trabajo. Se llama al CERRAR sesión:
 * el usuario que sale conserva su trabajo para la próxima vez, y el navegador
 * queda sin borradores de trabajo para el siguiente usuario.
 *
 * Sin `usuarioId`, degrada a `limpiarBorradoresLocales` (no hay a quién asociar
 * el respaldo, así que solo se aísla).
 * @param {string | null | undefined} usuarioId
 * @param {Storage} [almacen]
 * @returns {void}
 */
export function guardarBorradoresDeUsuario(usuarioId, almacen) {
  const storage = obtenerStorage(almacen);
  if (!storage) return;
  if (!usuarioId) {
    limpiarBorradoresLocales(storage);
    return;
  }

  const claves = recogerClavesDeTrabajo(storage);
  /** @type {Record<string, string>} */
  const respaldo = {};
  for (const clave of claves) {
    try {
      const valor = storage.getItem(clave);
      if (valor !== null) respaldo[clave] = valor;
    } catch {
      // clave ilegible: se omite del respaldo.
    }
  }

  const claveRespaldo = `${PREFIJO_RESPALDO_SESION}${usuarioId}`;
  try {
    if (Object.keys(respaldo).length > 0) {
      storage.setItem(claveRespaldo, JSON.stringify(respaldo));
    } else {
      // Sin borradores que guardar: se descarta cualquier respaldo previo para
      // no restaurar trabajo que el usuario ya vació.
      storage.removeItem(claveRespaldo);
    }
  } catch {
    // Cuota/almacenamiento: si no se puede respaldar, al menos se limpia abajo.
  }

  // Se limpian las claves de trabajo tanto si el respaldo se escribió como si no
  // (aislamiento para el siguiente usuario).
  limpiarBorradoresLocales(storage);
}

/**
 * RESTAURA los borradores de trabajo del usuario `usuarioId` desde su copia de
 * seguridad, tras LIMPIAR primero las de trabajo (aislamiento). Se llama al
 * INICIAR sesión: el mismo usuario recupera su trabajo; uno nuevo (sin
 * respaldo) entra con los editores en blanco. El respaldo restaurado se elimina
 * (pasa a estar "activo" en las claves de trabajo; se re-guardará al cerrar).
 *
 * Sin `usuarioId`, solo limpia (no hay respaldo que restaurar).
 * @param {string | null | undefined} usuarioId
 * @param {Storage} [almacen]
 * @returns {void}
 */
export function restaurarBorradoresDeUsuario(usuarioId, almacen) {
  const storage = obtenerStorage(almacen);
  if (!storage) return;

  // Aislamiento SIEMPRE primero: descarta cualquier borrador de trabajo que
  // quedara de otro usuario (p. ej. si cerró la pestaña sin cerrar sesión).
  limpiarBorradoresLocales(storage);
  if (!usuarioId) return;

  const claveRespaldo = `${PREFIJO_RESPALDO_SESION}${usuarioId}`;
  let bruto;
  try {
    bruto = storage.getItem(claveRespaldo);
  } catch {
    return;
  }
  if (bruto === null || bruto === undefined) return;

  /** @type {unknown} */
  let respaldo;
  try {
    respaldo = JSON.parse(bruto);
  } catch {
    try {
      storage.removeItem(claveRespaldo);
    } catch {
      /* ignore */
    }
    return;
  }
  if (typeof respaldo !== 'object' || respaldo === null) return;

  for (const [clave, valor] of Object.entries(/** @type {Record<string, unknown>} */ (respaldo))) {
    // Solo se restauran claves de trabajo legítimas (defensa ante un respaldo
    // manipulado): nunca se inyectan claves ajenas al namespace del editor.
    if (!esClaveDeTrabajo(clave) || typeof valor !== 'string') continue;
    try {
      storage.setItem(clave, valor);
    } catch {
      // Cuota: se omite esa clave; el resto se intenta igualmente.
    }
  }

  try {
    storage.removeItem(claveRespaldo);
  } catch {
    // Ignorado: el respaldo quedará y se sobrescribirá en el próximo logout.
  }
}
